import { getApiV1Url } from '@/config/api'
import { buildMemberSearchQueryString } from '@/utils/memberSearch'
import { formatProcessAt, signedRewardQuantity } from '@/utils/rewardDisplay'
import type { ProjectContext } from '@/utils/projectContext'

/** customers/{customerCode}/dashboard 등에서 쓰는 검색 상태 */
export type MemberSearchStatus =
  | 'NO_CUSTOMER'
  | 'NOT_SHOP_CUSTOMER'
  | 'SHOP_CUSTOMER'

/** customers/search — 간편가입 후속 API용 scope (found=false·간편가입 가능 시에만 내려옴) */
export type MemberCreateScope = 'OWN' | 'BRAND' | 'BOTH'

/** customers/search 응답의 고객 상태 */
export type MemberCustomerStatus = 'ACTIVE' | 'BLOCKED'

/** GET .../customers/search 응답 `data` */
export interface MemberSearchApiData {
  found: boolean
  customerCode: string
  nickname?: string
  /** found=true 일 때 내려옴 */
  status?: MemberCustomerStatus
  stage?: string
  deleted?: number
  platformFound: boolean
  platformCustomerCode?: string
  brandFound: boolean
  createScope?: MemberCreateScope
}

/** GET .../customers/{customerCode}/dashboard 응답 `data` (nickname·customerCode 없음) */
export interface MemberDashboardApiData {
  searchStatus: MemberSearchStatus
  stampCount: number
  pointAmount: number
  couponCount: number
}

/** 스탬프·포인트 유효기간 구분 (0: 일수, 1: 일시) */
export type MembershipValidType = 0 | 1

/** reward-policy 응답의 voucherSettings[] 한 건 */
export interface VoucherSetting {
  stampCount: number
  voucherCode: string
}

/** reward-policy 응답의 멤버십 한 건 */
export interface Membership {
  membershipCode: string
  title: string
  status: string
  startAt: string
  endAt: string
  rewardType: 'STAMP' | 'POINT' | 'BOTH'
  stampSetCount?: number
  voucherUseDelay?: number
  pointRatio?: number
  pointRoundingMode?: string
  pointRoundingScale?: number
  /** 적립 조건 제목 */
  rewardTitle?: string
  settlementOwner?: string
  validType?: MembershipValidType
  validPeriod?: number
  validAt?: string
  voucherExchangeMethod?: string
  pointRewardTarget?: string
  pointExpireCycle?: string
  voucherSettings?: VoucherSetting[]
  products?: MembershipProduct[]
}

/** reward-policy에서 추출한 멤버십 코드 (dashboard API 쿼리용) */
export interface MembershipCodes {
  stampMembershipCode?: string
  pointMembershipCode?: string
}

/** 멤버십 상품 (reward-policy 내 products[] 한 건) */
export interface MembershipProduct {
  name: string
  originalPrice?: number
}

/** reward-policy 응답 data */
export interface RewardPolicyData {
  memberships: Membership[]
}

/** accumulation/history 등에서 매핑한 최근 리워드(스탬프·포인트) 한 건 */
export interface RewardRecentItem {
  id: string
  rewardType: 'STAMP' | 'POINT'
  membershipCode: string
  shopCode: string
  rewardAmount: number
  rewardStatus: 'EARN' | 'EARN_CANCEL' | 'USE' | 'USE_CANCEL' | 'EXPIRE'
  rewardAt: string
}

/** POST .../customers/{customerCode}/rewards/earn 요청 body */
export type RewardsEarnRequest =
  | {
      rewardType: 'STAMP'
      brandCode: string
      membershipCode: string
      transactionId: string
      quantity: number
      orderId?: string
      receiptNo?: string
      receiptAt?: string
    }
  | {
      rewardType: 'POINT'
      brandCode: string
      membershipCode: string
      transactionId: string
      paymentAmount: number
      orderId?: string
      receiptNo?: string
      receiptAt?: string
    }

/** rewards/earn 응답의 rewards[] 한 건 */
export interface RewardsEarnRewardItem {
  id: number
  rewardType: 'STAMP' | 'POINT'
  membershipCode: string
  shopCode: string
  quantity: number
  status: string
  processAt: string
  expireAt?: string
  orderId?: string
  orderSourceType?: string
  transactionId?: string
  couponCode?: string
  receiptNo?: string
  receiptAt?: string
}

/** POST .../customers/{customerCode}/rewards/earn 응답 data */
export interface RewardsEarnData {
  transactionId: string
  balance: number
  status: string
  quantity: number
  rewards: RewardsEarnRewardItem[]
}

/** POST .../customers/{customerCode}/rewards/use 요청 body */
export interface RewardsUseRequest {
  rewardType: 'STAMP' | 'POINT'
  brandCode: string
  membershipCode: string
  transactionId: string
  quantity: number
  /** STAMP 등 쿠폰 사용 시. POINT 사용 시에는 미전달 */
  couponCode?: string
  orderId?: string
}

/** shops/.../customers/{customerCode}/rewards 응답의 rewards[] 한 건 */
export type AccumulationHistoryItem = RewardsEarnRewardItem

/** 적립 내역 목록 API 요청 파라미터 */
export interface AccumulationHistoryParams {
  recentDays?: number
  /** Spring 등 0-based 페이지 인덱스로 전달 */
  page: number
  size: number
  sort?: string
  signal?: AbortSignal
}

/** shops/.../customers/{customerCode}/rewards 응답 data */
export interface AccumulationHistoryData {
  totalCount: number
  rewards: AccumulationHistoryItem[]
  totalPages: number
  currentPage: number
  pageSize: number
  hasNext: boolean
  hasPrevious: boolean
}

/** POST .../rewards/cancel-earn/quantity 요청 body */
export interface RewardsCancelEarnQuantityRequest {
  rewardType: 'STAMP' | 'POINT'
  brandCode: string
  membershipCode: string
  transactionId: string
  quantity: number
}

/** POST .../rewards/cancel-use/quantity 요청 body */
export interface RewardsCancelUseQuantityRequest {
  rewardType: 'STAMP' | 'POINT'
  brandCode: string
  membershipCode: string
  transactionId: string
  quantity: number
  orderId?: string
}

/** 서버 공통 응답 (data만 사용) */
interface ApiResponse<T> {
  data?: T
  errors?: {
    error?: Array<{ code?: number; target?: string; message?: string }>
  }
}

/** reward-policy memberships[] → dashboard API용 멤버십 코드 */
export function membershipCodesFromPolicy(memberships: Membership[]): MembershipCodes {
  const codes: MembershipCodes = {}
  for (const m of memberships) {
    if (m.rewardType === 'STAMP' || m.rewardType === 'BOTH') {
      codes.stampMembershipCode = m.membershipCode
    }
    if (m.rewardType === 'POINT' || m.rewardType === 'BOTH') {
      codes.pointMembershipCode = m.membershipCode
    }
  }
  return codes
}

/**
 * GET /api/v1/projects/{projectCode}/memberships/reward-policy
 */
export async function getRewardPolicy(
  projectCode: string,
  brandCode: string
): Promise<Membership[]> {
  const url = getApiV1Url(
    `/projects/${projectCode}/memberships/reward-policy?brandCode=${encodeURIComponent(brandCode)}`
  )
  const response = await fetch(url)
  const json: ApiResponse<RewardPolicyData> = await response.json()

  if (!response.ok) {
    const message =
      json?.errors?.error?.[0]?.message ?? `요청 실패 (${response.status})`
    throw new Error(message)
  }

  if (json.data?.memberships == null) {
    throw new Error('멤버십 정보를 불러올 수 없습니다.')
  }

  return json.data.memberships
}

/**
 * GET /api/v1/projects/{projectCode}/customers/search
 */
export async function searchMember(
  projectCode: string,
  projectContext: ProjectContext,
  keyword: string
): Promise<MemberSearchApiData> {
  const query = buildMemberSearchQueryString(projectContext, keyword)
  const url = getApiV1Url(
    `/projects/${projectCode}/customers/search?${query}`
  )
  const response = await fetch(url)
  const json: ApiResponse<MemberSearchApiData> = await response.json()

  if (!response.ok) {
    const message =
      json?.errors?.error?.[0]?.message ?? `요청 실패 (${response.status})`
    throw new Error(message)
  }

  if (json.data == null) {
    throw new Error('회원 검색 결과를 불러올 수 없습니다.')
  }

  return json.data
}

/** POST .../customers 요청 body (scope별 선택 필드) */
export interface CustomerCreateRequestBody {
  scope: MemberCreateScope
  phone?: string
  platformProjectCode?: string
  platformCustomerCode?: string
}

/** customers 생성 응답의 고객 한 건 */
export interface ProjectCustomerDto {
  projectCode: string
  code: string
  status: string
  stage: string
  entryGuestMode: string
  nickname: string
  email?: string
  phone?: string
  platformProjectCode?: string
  platformCustomerCode?: string
  createdAt?: string
  updatedAt?: string
}

/** POST .../customers 응답 `data` */
export interface CustomerCreateApiData {
  ownProjectCustomer?: ProjectCustomerDto
  brandProjectCustomer?: ProjectCustomerDto
}

/**
 * POST /api/v1/projects/{projectCode}/customers
 * (간편가입 고객 생성)
 */
export async function createProjectCustomer(
  projectCode: string,
  body: CustomerCreateRequestBody
): Promise<CustomerCreateApiData> {
  const url = getApiV1Url(`/projects/${encodeURIComponent(projectCode)}/customers`)
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  })
  const json: ApiResponse<CustomerCreateApiData> = await response.json()

  if (!response.ok) {
    const message =
      json?.errors?.error?.[0]?.message ?? `간편가입 요청 실패 (${response.status})`
    throw new Error(message)
  }

  if (json.data == null) {
    throw new Error('간편가입 후 회원 정보를 불러올 수 없습니다.')
  }

  return json.data
}

/**
 * GET /api/v1/projects/{projectCode}/customers/{customerCode}/dashboard
 */
export async function getMemberDashboard(
  projectCode: string,
  shopCode: string,
  customerCode: string,
  membershipCodes?: MembershipCodes
): Promise<MemberDashboardApiData> {
  const search = new URLSearchParams()
  search.set('shopCode', shopCode)
  if (membershipCodes?.stampMembershipCode) {
    search.set('stampMembershipCode', membershipCodes.stampMembershipCode)
  }
  if (membershipCodes?.pointMembershipCode) {
    search.set('pointMembershipCode', membershipCodes.pointMembershipCode)
  }
  const url = getApiV1Url(
    `/projects/${projectCode}/customers/${encodeURIComponent(customerCode)}/dashboard?${search.toString()}`
  )
  const response = await fetch(url)
  const json: ApiResponse<MemberDashboardApiData> = await response.json()

  if (!response.ok) {
    const message =
      json?.errors?.error?.[0]?.message ?? `요청 실패 (${response.status})`
    throw new Error(message)
  }

  if (json.data == null) {
    throw new Error('회원 정보를 불러올 수 없습니다.')
  }

  return json.data
}

/**
 * POST /api/v1/projects/{projectCode}/shops/{shopCode}/customers/{customerCode}/rewards/earn
 */
export async function postRewardsEarn(
  projectCode: string,
  shopCode: string,
  customerCode: string,
  body: RewardsEarnRequest
): Promise<RewardsEarnData> {
  const url = getApiV1Url(
    `/projects/${projectCode}/shops/${shopCode}/customers/${encodeURIComponent(customerCode)}/rewards/earn`
  )
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  })
  const json: ApiResponse<RewardsEarnData> = await response.json()

  if (!response.ok) {
    const message =
      json?.errors?.error?.[0]?.message ?? `요청 실패 (${response.status})`
    throw new Error(message)
  }

  if (json.data == null) {
    throw new Error('적립 결과를 불러올 수 없습니다.')
  }

  return json.data
}

/**
 * POST /api/v1/projects/{projectCode}/shops/{shopCode}/customers/{customerCode}/rewards/use
 */
export async function postRewardsUse(
  projectCode: string,
  shopCode: string,
  customerCode: string,
  body: RewardsUseRequest
): Promise<RewardsEarnData> {
  const url = getApiV1Url(
    `/projects/${projectCode}/shops/${shopCode}/customers/${encodeURIComponent(customerCode)}/rewards/use`
  )
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  })
  const json: ApiResponse<RewardsEarnData> = await response.json()

  if (!response.ok) {
    const message =
      json?.errors?.error?.[0]?.message ?? `요청 실패 (${response.status})`
    throw new Error(message)
  }

  if (json.data == null) {
    throw new Error('사용 처리 결과를 불러올 수 없습니다.')
  }

  return json.data
}

/**
 * GET /api/v1/projects/{projectCode}/shops/{shopCode}/customers/{customerCode}/rewards
 */
export async function getAccumulationHistory(
  projectCode: string,
  shopCode: string,
  customerCode: string,
  brandCode: string,
  params: AccumulationHistoryParams
): Promise<AccumulationHistoryData> {
  const search = new URLSearchParams()
  search.set('brandCode', brandCode)
  const recentDays = params.recentDays ?? 93
  search.set('recentDays', String(recentDays))
  search.set('page', String(params.page))
  search.set('size', String(params.size))
  if (params.sort != null) search.set('sort', params.sort)

  const url = getApiV1Url(
    `/projects/${projectCode}/shops/${shopCode}/customers/${encodeURIComponent(customerCode)}/rewards?${search.toString()}`
  )
  const response = await fetch(
    url,
    params.signal != null ? { signal: params.signal } : undefined
  )
  const json: ApiResponse<AccumulationHistoryData> = await response.json()

  if (!response.ok) {
    const message =
      json?.errors?.error?.[0]?.message ?? `요청 실패 (${response.status})`
    throw new Error(message)
  }

  if (json.data == null) {
    throw new Error('적립 내역 목록을 불러올 수 없습니다.')
  }

  return json.data
}

function mapAccumulationHistoryToRewardRecent(
  item: AccumulationHistoryItem
): RewardRecentItem {
  const rewardStatus = item.status as RewardRecentItem['rewardStatus']
  return {
    id: String(item.id),
    rewardType: item.rewardType,
    membershipCode: item.membershipCode,
    shopCode: item.shopCode,
    rewardAmount: signedRewardQuantity(item.quantity, item.status),
    rewardStatus,
    rewardAt: formatProcessAt(item.processAt)
  }
}

/**
 * 최근 리워드 내역(스탬프·포인트) 상한 limit건.
 * `shops/.../customers/{customerCode}/rewards` (page=0, size=limit)를 사용한다.
 */
export async function getRewardRecent(
  projectCode: string,
  shopCode: string,
  customerCode: string,
  brandCode: string,
  limit: number = 5
): Promise<RewardRecentItem[]> {
  const data = await getAccumulationHistory(
    projectCode,
    shopCode,
    customerCode,
    brandCode,
    { page: 0, size: limit }
  )
  return data.rewards.map(mapAccumulationHistoryToRewardRecent)
}

/**
 * POST .../customers/{customerCode}/rewards/cancel-earn/quantity
 * (수량 기반 적립 취소)
 */
export async function postRewardsCancelEarnQuantity(
  projectCode: string,
  shopCode: string,
  customerCode: string,
  body: RewardsCancelEarnQuantityRequest
): Promise<RewardsEarnData> {
  const url = getApiV1Url(
    `/projects/${projectCode}/shops/${shopCode}/customers/${encodeURIComponent(customerCode)}/rewards/cancel-earn/quantity`
  )
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  })
  const json: ApiResponse<RewardsEarnData> = await response.json()

  if (!response.ok) {
    const message =
      json?.errors?.error?.[0]?.message ?? `요청 실패 (${response.status})`
    throw new Error(message)
  }

  if (json.data == null) {
    throw new Error('적립 취소 결과를 불러올 수 없습니다.')
  }

  return json.data
}

/**
 * POST .../customers/{customerCode}/rewards/cancel-use/quantity
 * (수량 기반 사용 취소)
 */
export async function postRewardsCancelUseQuantity(
  projectCode: string,
  shopCode: string,
  customerCode: string,
  body: RewardsCancelUseQuantityRequest
): Promise<RewardsEarnData> {
  const url = getApiV1Url(
    `/projects/${projectCode}/shops/${shopCode}/customers/${encodeURIComponent(customerCode)}/rewards/cancel-use/quantity`
  )
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  })
  const json: ApiResponse<RewardsEarnData> = await response.json()

  if (!response.ok) {
    const message =
      json?.errors?.error?.[0]?.message ?? `요청 실패 (${response.status})`
    throw new Error(message)
  }

  if (json.data == null) {
    throw new Error('사용 취소 결과를 불러올 수 없습니다.')
  }

  return json.data
}
